
import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';



interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prev: FormData) => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setIsSubmitting(true);
      


      const { error } = await supabase.functions.invoke('contact-form', {
        body: JSON.stringify(formData)
      });

      if (error) {
        console.error('Error from edge function:', error);
        throw new Error(error.message || 'Something went wrong');
      }



      toast.success("Thanks for reaching out! We'll be in touch soon.");
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error: unknown) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-primary mb-1">
            Name <span className="text-accent">*</span>
          </label>
          <Input 
            id="name" 
            placeholder="Your name" 
            value={formData.name}
            onChange={handleChange}
            required
            className="border-border focus:border-secondary focus:ring-secondary"
          />
        </div>
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-primary mb-1">
            Email <span className="text-accent">*</span>
          </label>
          <Input 
            id="email" 
            type="email" 
            placeholder="Your email"
            value={formData.email}
            onChange={handleChange} 
            required
            className="border-border focus:border-secondary focus:ring-secondary"
          />
        </div>
      </div>
      
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-primary mb-1">
          Phone Number (optional)
        </label>
        <Input 
          id="phone" 
          type="tel" 
          placeholder="Your phone number"
          value={formData.phone}
          onChange={handleChange}
          className="border-border focus:border-secondary focus:ring-secondary"
        />
      </div>
      
      <div>
        <label htmlFor="subject" className="block text-sm font-medium text-primary mb-1">
          Subject
        </label>
        <Input 
          id="subject" 
          placeholder="How can we help you?"
          value={formData.subject}
          onChange={handleChange}
          className="border-border focus:border-secondary focus:ring-secondary"
        />
      </div>
      
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-primary mb-1">
          Message <span className="text-accent">*</span>
        </label>
        <Textarea 
          id="message" 
          placeholder="Tell us more about your inquiry..." 
          rows={5}
          value={formData.message}
          onChange={handleChange}
          required
          className="border-border focus:border-secondary focus:ring-secondary"
        />
      </div>
      
      <Button 
        type="submit" 
        className="w-full bg-secondary hover:bg-secondary/90 text-white" 
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>Processing...</>
        ) : (
          <>
            <Send className="mr-2 h-4 w-4" />
            Send Message
          </>
        )}
      </Button>
    </form>
  );
};

export default ContactForm;
